import {TargetId} from '~/entities/operations-fiat';
import {TradeOptionView, type CurrencyOptionView} from '~/processes/dashboard/CurrencySelectionPage/interfaces';
import {OperationAlias, OperationModel} from '../../domain';
import {getAllOperations} from '../../financesPrimitives';
import {selectFinancesResource} from '../../financesResource';
import {goToOperation} from '../../financesRouting';

const getOperationsResource = (store: AppModel) => selectFinancesResource(store).mapSuccess(getAllOperations);

export const _trade = (state: AppModel) => getOperationsResource(state).mapSuccess(getTradeOptions);

export const _send = (store: AppModel) =>
    getOperationsResource(store).mapSuccess(getViewsWhere(({type}) => type === 'withdraw'));

export const _receive = (state: AppModel) =>
    getOperationsResource(state).mapSuccess(getViewsWhere(({type}) => type === 'topup'));

export const _topupFiat = (store: AppModel) =>
    getOperationsResource(store).mapSuccess(
        getViewsWhere(({type, account}) => type === 'topup' && account.type === 'fiat')
    );

export const _buyCrypto = (state: AppModel) => getOperationsResource(state).mapSuccess(getCryptoBuyOptions);

export const _exchange = (store: AppModel) =>
    getOperationsResource(store)
        // type might be exchange but alias is sell or buy, we need only exchange
        .mapSuccess(getViewsWhere(operation => OperationAlias.from(operation) === 'exchange'));

const getViewsWhere =
    (matchOperation: (op: OperationModel) => boolean) =>
    (operations: OperationModel[]): CurrencyOptionView[] => {
        const alreadyIncluded = new Set<TargetId>();
        return operations
            .filter(op => {
                const id = TargetId.from(op.account.id, op.account.type);
                if (matchOperation(op) && !alreadyIncluded.has(id)) {
                    alreadyIncluded.add(id);
                    return true;
                }
                return false;
            })
            .map(CurrencyOptionView.fromOperation);
    };

const CurrencyOptionView = {
    fromOperation: (operation: OperationModel) => {
        const account = OperationModel.getMainAccount(operation);
        return {
            icon: account.icon,
            name: account.currencyName,
            shortName: account.currencyCode,
            clickAction: goToOperation(operation),
            accountType: account.type,
            providerId: account.providerId,
        };
    },
};

const getCryptoBuyOptions = (operations: OperationModel[]): CurrencyOptionView[] =>
    operations.filter(OperationModel.isBuyOperation).map(CurrencyOptionView.fromOperation);

const getTradeOptions = (operations: OperationModel[]): TradeOptionView[] => [
    ...(function* () {
        const alreadyIncludedIn: {[K in TradeOptionView['operationName']]: Set<TargetId>} = {
            exchange: new Set(),
            sell: new Set(),
            buy: new Set(),
        };
        for (const operation of operations) {
            const alias = OperationAlias.from(operation);
            const account = OperationModel.getMainAccount(operation);
            if (alias === 'buy' || alias === 'sell' || alias === 'exchange') {
                const id = TargetId.from(account.id, account.type);
                if (alreadyIncludedIn[alias].has(id)) continue;
                else alreadyIncludedIn[alias].add(id);
                yield {...CurrencyOptionView.fromOperation(operation), operationName: alias};
            }
        }
    })(),
];
